import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { progressReportAPI } from '../../utils/api';
import { ProgressReport } from '../../types/progressReport';
import { MdRefresh, MdChevronRight, MdTrendingUp, MdSchool, MdLightbulb } from 'react-icons/md';

const Refresh = MdRefresh as any;
const ChevronRight = MdChevronRight as any;
const TrendingUp = MdTrendingUp as any;
const School = MdSchool as any;
const Lightbulb = MdLightbulb as any;

export default function ProgressReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ProgressReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await progressReportAPI.getReports(10, 0);
      setReports(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching progress reports:', err);
      setError(err.message || 'Failed to load progress reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      setError(null);

      const newReport = await progressReportAPI.generateReport({
        report_type: 'custom',
        days: 30,
      });

      // Refresh the list
      await fetchReports();

      // Navigate to the detail page
      navigate(`/progress-reports/${newReport.id}`);
    } catch (err: any) {
      console.error('Error generating report:', err);
      setError(err.message || 'Failed to generate progress report');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getReportTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      trial_day_15: 'Trial Day 15',
      trial_day_25: 'Trial Day 25',
      weekly: 'Weekly Summary',
      monthly: 'Monthly Summary',
      custom: 'Custom Report',
    };
    return labels[type] || type;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Progress Reports</h1>
              <p className="text-gray-400">
                AI-powered insights into your learning journey
              </p>
            </div>
            <button
              onClick={handleGenerateReport}
              disabled={generating}
              className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Refresh className={generating ? 'animate-spin' : ''} size={20} />
              {generating ? 'Generating...' : 'Generate New Report'}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-600 rounded-lg p-4 mb-6">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : reports.length === 0 ? (
            /* Empty State */
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-12 text-center">
              <School size={64} className="text-gray-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">No Progress Reports Yet</h2>
              <p className="text-gray-400 mb-6">
                Complete some lessons and generate your first progress report to see AI-powered insights into your learning.
              </p>
              <button
                onClick={handleGenerateReport}
                disabled={generating}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
              >
                Generate Your First Report
              </button>
            </div>
          ) : (
            /* Reports List */
            <div className="space-y-4">
              {reports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => navigate(`/progress-reports/${report.id}`)}
                  className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-6 hover:border-primary/50 transition cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold text-white">
                          {getReportTypeLabel(report.report_type)}
                        </h3>
                        <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
                          {formatDate(report.generated_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">
                        {formatDate(report.date_range_start)} - {formatDate(report.date_range_end)}
                      </p>
                    </div>
                    <ChevronRight size={24} className="text-gray-600" />
                  </div>

                  {/* Summary */}
                  <p className="text-gray-300 mb-4 line-clamp-2">{report.summary}</p>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp size={16} className="text-green-400" />
                        <span className="text-xs text-gray-400">Concepts Mastered</span>
                      </div>
                      <p className="text-lg font-semibold text-white">
                        {report.concepts_mastered.length}
                      </p>
                    </div>

                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <School size={16} className="text-blue-400" />
                        <span className="text-xs text-gray-400">Subjects</span>
                      </div>
                      <p className="text-lg font-semibold text-white">
                        {Object.keys(report.subject_breakdown).length}
                      </p>
                    </div>

                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Lightbulb size={16} className="text-yellow-400" />
                        <span className="text-xs text-gray-400">Key Moments</span>
                      </div>
                      <p className="text-lg font-semibold text-white">
                        {report.notable_moments.length}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
